import type { CSSProperties, ReactNode } from 'react'

// ── SortArrow ─────────────────────────────────────────────────────────────────
// Exported for use in data-driven tables (e.g. InventarioTable)

interface SortArrowProps {
  active: boolean
  dir: 'asc' | 'desc'
  dark: boolean
}

export function SortArrow({ active, dir, dark }: SortArrowProps) {
  if (!active) {
    return (
      <svg
        className={`w-3 h-3 shrink-0 ${dark ? 'text-white/22' : 'text-slate-300'}`}
        viewBox="0 0 12 12" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M3.5 4.5L6 2l2.5 2.5" />
        <path d="M3.5 7.5L6 10l2.5-2.5" />
      </svg>
    )
  }
  return (
    <svg
      className="w-3 h-3 shrink-0 text-sky-500"
      viewBox="0 0 12 12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      {dir === 'asc'
        ? <path d="M2.5 8L6 4l3.5 4" />
        : <path d="M2.5 4L6 8l3.5-4" />
      }
    </svg>
  )
}

// ── SortDirButton ─────────────────────────────────────────────────────────────
// Small standalone button showing current sort direction (for toolbar usage)

interface SortDirButtonProps {
  dark: boolean
  dir: 'asc' | 'desc'
  onClick: () => void
  className?: string
  title?: string
}

export function SortDirButton({ dark, dir, onClick, className = '', title }: SortDirButtonProps) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`flex items-center justify-center w-7 h-7 rounded-lg border transition-colors cursor-pointer shrink-0 ${
        dark
          ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
          : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm'
      } ${className}`}
    >
      <svg
        className="w-3.5 h-3.5"
        viewBox="0 0 14 14" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      >
        {dir === 'asc'
          ? <path d="M3 9l4-4 4 4" />
          : <path d="M3 5l4 4 4-4" />
        }
      </svg>
    </button>
  )
}

// ── SortableHeader ────────────────────────────────────────────────────────────

interface SortableHeaderProps {
  dark: boolean
  label: ReactNode
  sortKey: string
  activeSortKey: string
  activeSortDir: 'asc' | 'desc'
  onClick: () => void
  className?: string
  align?: 'left' | 'right'
  /**
   * Controles extra junto al indicador de orden (p. ej. el botón Σ de totales).
   * El `<th>` lleva la clase `group`, así que pueden aparecer sólo al hover.
   */
  extra?: ReactNode
  /**
   * Estilo en línea del `<th>`. Necesario para las columnas congeladas, cuyo
   * desplazamiento izquierdo se calcula a partir de los anchos y no cabe en una
   * clase de Tailwind.
   */
  style?: CSSProperties
  /**
   * ¿El rótulo puede partir en varias líneas?
   *
   * Por defecto NO: la mayoría de tablas reparten el ancho automáticamente y el
   * `nowrap` es justo lo que obliga a cada columna a medir su rótulo entero.
   * Pero una tabla de ancho fijo no crece para el rótulo, así que el rótulo
   * largo se sale de su columna y la siguiente lo tapa con su fondo opaco: es lo
   * que dejaba «TIPO DE ARTÍCU» a medias. Ahí hay que partirlo.
   *
   * No basta con pasar `whitespace-normal` en `className`: las dos utilidades
   * tienen la misma especificidad y decide el orden de la HOJA —donde `nowrap`
   * va después—, no el del atributo. Por eso es una prop y no una clase.
   */
  wrap?: boolean
}

export function SortableHeader({
  dark,
  label,
  sortKey,
  activeSortKey,
  activeSortDir,
  onClick,
  className = '',
  align = 'left',
  extra,
  style,
  wrap = false,
}: SortableHeaderProps) {
  const isActive = sortKey === activeSortKey
  const colorCls = dark
    ? 'text-slate-400 hover:text-slate-200'
    : 'text-slate-500 hover:text-slate-700'

  return (
    <th
      className={`group px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider select-none cursor-pointer transition-colors ${
        wrap ? 'whitespace-normal leading-tight' : 'whitespace-nowrap'
      } ${colorCls} ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${className}`}
      style={style}
      onClick={onClick}
    >
      {/* Partiendo el rótulo, los iconos NO pueden ser hermanos flex del texto:
          el texto se queda la línea entera y la flecha baja sola a una tercera.
          Van en el flujo del texto, como una unidad inline que sigue a la última
          palabra — y en las numéricas eso es el borde derecho, donde estaban. */}
      {wrap ? (
        <>
          {label}{' '}
          <span className="inline-flex items-center gap-1 align-middle">
            <SortArrow active={isActive} dir={isActive ? activeSortDir : 'asc'} dark={dark} />
            {extra}
          </span>
        </>
      ) : (
        <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
          {label}
          <SortArrow active={isActive} dir={isActive ? activeSortDir : 'asc'} dark={dark} />
          {extra}
        </span>
      )}
    </th>
  )
}
